import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { openAuthDownload } from "../lib/download";
import { formatINR } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { CollectionReport } from "../components/CollectionReport";

function ReportTable({ headers, rows, summary, isMoney }) {
    return (
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
            {summary && (
                <div className="grid sm:grid-cols-3 gap-3 p-4 bg-blue-50/40 border-b border-blue-100">
                    {Object.entries(summary).map(([k, v]) => (
                        <div key={k}>
                            <div className="text-[10px] uppercase tracking-wider text-blue-900 font-semibold">{k}</div>
                            <div className="num font-bold text-slate-900 mt-0.5">
                                {typeof v === "number" ? formatINR(v) : v}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="overflow-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            {headers.map((h) => (
                                <th key={h} className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={headers.length} className="text-center text-slate-400 py-8 text-sm">No data</td></tr>
                        ) : rows.map((r, i) => (
                            <tr key={`row-${i}`} className="hover:bg-slate-50">
                                {r.map((c, j) => (
                                    <td key={`cell-${i}-${j}`} className={`px-4 py-2 text-slate-800 ${typeof c === "number" ? "text-right num font-semibold" : ""}`}>
                                        {typeof c === "number" && isMoney?.(j) ? formatINR(c) : String(c)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

const reports = [
    { key: "collections", label: "Collections", moneyCol: (j) => j === 8 },
    { key: "pnl-monthly", label: "Monthly P&L", path: "/reports/pnl?period=monthly", moneyCol: (j) => j === 1 },
    { key: "pnl-yearly", label: "Yearly P&L", path: "/reports/pnl?period=yearly", moneyCol: (j) => j === 1 },
    { key: "outstanding", label: "Outstanding Loans", path: "/reports/outstanding", moneyCol: (j) => j === 5 },
    { key: "overdue", label: "Overdue Customers", path: "/reports/overdue", moneyCol: (j) => j === 6 },
    { key: "investor-payouts", label: "Investor Payouts", path: "/reports/investor-payouts", moneyCol: (j) => j === 4 },
    { key: "daily-collection", label: "Today's Collections", path: "/reports/daily-collection", moneyCol: (j) => j === 5 },
];

const collectionTable = (d) => (
    <ReportTable headers={d.headers} rows={d.rows} summary={d.summary} isMoney={(j) => j === 8} />
);

export default function Reports() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [active, setActive] = useState(reports[0].key);
    const [data, setData] = useState(null);

    useEffect(() => {
        const r = reports.find((x) => x.key === active);
        if (!r.path) return;
        setData(null);
        api.get(r.path).then((res) => setData(res.data));
    }, [active]);

    const r = reports.find((x) => x.key === active);

    if (!isAdmin) {
        return (
            <div className="space-y-5">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">My Collection Reports</h1>
                <CollectionReport renderTable={collectionTable} />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Reports</h1>
                {r.path && (
                <div className="flex gap-2">
                    <Button variant="outline" className="h-10 rounded-xl" data-testid="export-pdf-btn"
                        onClick={() => openAuthDownload(`/reports/${active}/export?fmt=pdf`)}>
                        <FileDown className="h-4 w-4 mr-1.5" /> PDF
                    </Button>
                    <Button variant="outline" className="h-10 rounded-xl" data-testid="export-excel-btn"
                        onClick={() => openAuthDownload(`/reports/${active}/export?fmt=excel`)}>
                        <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
                    </Button>
                </div>
                )}
            </div>

            <Tabs value={active} onValueChange={setActive}>
                <TabsList className="bg-white border border-slate-100 rounded-xl flex-wrap h-auto">
                    {reports.map((x) => (
                        <TabsTrigger key={x.key} value={x.key} data-testid={`report-${x.key}`}>
                            {x.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                <TabsContent value={active} className="mt-4">
                    {!r.path ? (
                        <CollectionReport renderTable={collectionTable} />
                    ) : data ? (
                        <ReportTable headers={data.headers} rows={data.rows} summary={data.summary} isMoney={r.moneyCol} />
                    ) : (
                        <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">Loading…</Card>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
